import { useEffect, useMemo, useState } from 'react';
import type { CustomerTransferApprovalPayload, CustomerTransferMode } from '@nadoutong/contracts';
import { useCreateCustomerTransferRequest, useTransferCandidates } from '../api/approvals.ts';
import { OpsApiError } from '../api/client.ts';
import { ROLE_OPS_ADMIN, hasAnyOpsRole } from '../auth/opsRoles.ts';
import { useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import { Button, Dialog, FormField, Input, RadioGroup, Select } from '../ui/index.ts';
import {
  buildOpsUserPickerOptions,
  duplicateDisplayNameIds,
  opsUserPickerIdentityFromCandidate,
} from '../lib/opsUserPickerLabel.ts';
import { CustomerCompanyLine } from './identity/CustomerCell.tsx';
import { useToast } from './Toast.tsx';

/**
 * Creation dialog for `customer_transfer` approval requests (epic #1030
 * E1-S5, #1041): release an owned customer back to the pool, or transfer
 * them to a same-brand customer manager. The submission goes through the
 * approval center — a same-brand sales lead decides it; nothing changes
 * until approve.
 *
 * The target picker is restricted server-side to same-brand customer
 * managers (ops-api pins sales sessions to their profile brand); the
 * requester is additionally filtered out client-side because a transfer
 * may not name its own requester.
 */

interface CustomerTransferRequestDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly orgId: string;
  readonly customerEmail: string;
  /** Customer signup brand — forwarded as the candidates filter for admin sessions. */
  readonly customerBrand: string;
}

export function CustomerTransferRequestDialog({
  open,
  onOpenChange,
  orgId,
  customerEmail,
  customerBrand,
}: CustomerTransferRequestDialogProps): JSX.Element {
  const t = useT();
  const toast = useToast();
  const { user } = useSession();
  const isAdmin = hasAnyOpsRole(user?.roles ?? [], [ROLE_OPS_ADMIN]);
  const selfId = user?.opsUserId ?? '';

  const [mode, setMode] = useState<CustomerTransferMode>('release');
  const [targetId, setTargetId] = useState('');
  const [reason, setReason] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setMode('release');
      setTargetId('');
      setReason('');
      setValidationError(null);
    }
  }, [open]);

  const candidates = useTransferCandidates(isAdmin ? customerBrand : undefined, open);
  const targetIdentities = useMemo(
    () =>
      (candidates.data?.candidates ?? [])
        .filter((candidate) => candidate.ops_user_id !== selfId)
        .map(opsUserPickerIdentityFromCandidate),
    [candidates.data, selfId],
  );
  const targetOptions = useMemo(
    () => buildOpsUserPickerOptions(targetIdentities, t),
    [targetIdentities, t],
  );
  const duplicateIds = useMemo(
    () => duplicateDisplayNameIds(targetIdentities),
    [targetIdentities],
  );
  const selectedDuplicate = targetIdentities.find(
    (identity) => identity.id === targetId && duplicateIds.has(identity.id),
  );
  const selectedDuplicateName = selectedDuplicate?.displayName?.trim() ?? '';

  const create = useCreateCustomerTransferRequest();

  function submit(): void {
    if (mode === 'transfer' && targetId.length === 0) {
      setValidationError(t('customer_transfer.target.required'));
      return;
    }
    setValidationError(null);
    const payload: CustomerTransferApprovalPayload = { mode };
    if (mode === 'transfer') {
      payload.to_ops_user_id = targetId;
    }
    const trimmedReason = reason.trim();
    if (trimmedReason.length > 0) {
      payload.reason = trimmedReason;
    }
    create.mutate(
      { orgId, payload },
      {
        onSuccess: () => {
          toast.success(t('customer_transfer.toast.submitted'));
          onOpenChange(false);
        },
      },
    );
  }

  const submitError =
    create.error !== null
      ? create.error instanceof OpsApiError
        ? create.error.code === 'APPROVAL_DUPLICATE_OPEN_REQUEST'
          ? t('customer_transfer.error.duplicate')
          : `${create.error.code}: ${create.error.message}`
        : create.error.message
      : null;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!create.isPending) {
          onOpenChange(next);
        }
      }}
      title={t('customer_transfer.dialog.title')}
      description={t('customer_transfer.dialog.description')}
      closeLabel={t('common.close')}
      testId="customer-transfer-dialog"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={create.isPending}
            data-testid="customer-transfer-cancel"
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            loading={create.isPending}
            data-testid="customer-transfer-submit"
          >
            {t('customer_transfer.submit')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1 rounded-lg bg-well px-3 py-2.5">
          <span className="text-label text-paper-low">{t('customer_transfer.dialog.customer')}</span>
          <CustomerCompanyLine orgId={orgId} />
          <span className="text-body font-medium text-paper">{customerEmail}</span>
          <span className="font-mono text-micro text-paper-low">{orgId}</span>
        </div>

        <FormField label={t('customer_transfer.mode.label')} fieldId="customer-transfer-mode">
          <RadioGroup
            value={mode}
            onValueChange={(value) => {
              if (value === 'release' || value === 'transfer') {
                setMode(value);
                setValidationError(null);
              }
            }}
            ariaLabel={t('customer_transfer.mode.label')}
            testId="customer-transfer-mode"
            options={[
              {
                value: 'release',
                label: t('customer_transfer.mode.release'),
                hint: t('customer_transfer.mode.release_hint'),
              },
              {
                value: 'transfer',
                label: t('customer_transfer.mode.transfer'),
                hint: t('customer_transfer.mode.transfer_hint'),
              },
            ]}
          />
        </FormField>

        {mode === 'transfer' ? (
          <FormField
            label={t('customer_transfer.target.label')}
            fieldId="customer-transfer-target"
            required
            error={validationError}
            hint={targetOptions.length === 0 && candidates.isSuccess ? t('customer_transfer.target.empty') : undefined}
          >
            <Select
              value={targetId.length > 0 ? targetId : undefined}
              onValueChange={(value) => {
                setTargetId(value);
                setValidationError(null);
              }}
              options={targetOptions}
              placeholder={t('customer_transfer.target.placeholder')}
              disabled={candidates.isPending || targetOptions.length === 0}
              ariaLabel={t('customer_transfer.target.label')}
              testId="customer-transfer-target"
            />
            {selectedDuplicate !== undefined && selectedDuplicateName.length > 0 ? (
              <p
                className="m-0 text-dense text-warn"
                data-testid="customer-transfer-target-duplicate-warning"
              >
                {t('customer_transfer.target.duplicate_name_warning', {
                  name: selectedDuplicateName,
                })}
              </p>
            ) : null}
          </FormField>
        ) : null}

        <FormField
          label={t('customer_transfer.reason.label')}
          fieldId="customer-transfer-reason"
          hint={t('customer_transfer.reason.hint')}
        >
          <Input
            id="customer-transfer-reason"
            value={reason}
            onChange={(event) => setReason(event.currentTarget.value)}
            placeholder={t('customer_transfer.reason.placeholder')}
            maxLength={500}
            data-testid="customer-transfer-reason"
          />
        </FormField>

        {submitError !== null ? (
          <p
            role="alert"
            data-testid="customer-transfer-error"
            className="m-0 rounded-lg border border-bad-line bg-bad-dim/50 px-3 py-2 text-dense text-bad-bright"
          >
            {submitError}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
